import { motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGameStore } from '@/store/gameStore';
import { getAllVerses, getVerseBackdrop, getVerseThumbnail } from '@/data/characters';
import { CommunityVerseNotice, communityVerseName, isCommunityVerse } from '@/components/CommunityVerseNotice';
import { Button } from '@/components/ui/button';
import { BrandCorner } from '@/components/BrandCorner';
import { Footer } from '@/components/Footer';
import { CREATOR_VERSES_CHANGED_EVENT } from '@/lib/localCreatorStore';

export function HomeScreen() {
  const { setPhase, updateSettings, settings } = useGameStore();
  const navigate = useNavigate();
  const [creatorVersion, setCreatorVersion] = useState(0);
  const verses = useMemo(() => getAllVerses(), [creatorVersion]);

  useEffect(() => {
    const handleCreatorChange = () => setCreatorVersion((version) => version + 1);
    window.addEventListener(CREATOR_VERSES_CHANGED_EVENT, handleCreatorChange);
    return () => window.removeEventListener(CREATOR_VERSES_CHANGED_EVENT, handleCreatorChange);
  }, []);

  // Community (Creator) verses show the notice as soon as one is picked, like Anime Draft,
  // and again before a game if it wasn't acknowledged.
  const [noticeFor, setNoticeFor] = useState<'cpu-setup' | 'lobby' | 'select' | null>(null);
  const [acknowledgedVerse, setAcknowledgedVerse] = useState<string | null>(null);
  const [previousVerse, setPreviousVerse] = useState<string | null>(null);
  const needsNotice = (verse: string) => isCommunityVerse(verse) && acknowledgedVerse !== verse;

  const selectVerse = (verse: string) => {
    if (verse === settings.verse) return;
    setPreviousVerse(settings.verse);
    updateSettings({ verse });
    if (needsNotice(verse)) setNoticeFor('select');
  };

  const go = (phase: 'cpu-setup' | 'lobby') => {
    if (needsNotice(settings.verse)) setNoticeFor(phase);
    else setPhase(phase);
  };

  const handlePlayVsCpu = () => go('cpu-setup');

  const handleOpenLobby = () => go('lobby');



  return (
    <div className="min-h-screen bg-arena flex flex-col items-center justify-center p-6 relative overflow-hidden">
      <BrandCorner />
      {noticeFor && (
        <CommunityVerseNotice
          verseName={communityVerseName(settings.verse)}
          onContinue={() => {
            const phase = noticeFor;
            setNoticeFor(null);
            setAcknowledgedVerse(settings.verse);
            if (phase !== 'select') setPhase(phase);
          }}
          onBack={() => {
            // Backing out of a freshly picked community verse goes back to the previous verse.
            if (noticeFor === 'select' && previousVerse) updateSettings({ verse: previousVerse });
            setNoticeFor(null);
          }}
        />
      )}
      
      {/* Backdrop of the selected verse, dimmed and faded into the navy page. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[90vh] overflow-hidden [mask-image:linear-gradient(to_bottom,black_25%,transparent_100%)]"
      >
        <img
          key={settings.verse}
          src={getVerseBackdrop(settings.verse)}
          alt=""
          className="h-full w-full object-cover opacity-[0.14] saturate-[0.7]"
        />
      </div>

      {/* Title */}
      <motion.div
        className="text-center mb-6 sm:mb-10 mt-16 sm:mt-6 relative z-10"
        initial={false}
      >
        <h1 className="font-display font-black text-3xl sm:text-4xl md:text-6xl text-foreground mb-3 [text-shadow:0_2px_0_rgba(0,0,0,0.45)]">
          ANIME-DRAFT
          <br />
          <span className="text-primary">AUCTION</span>
        </h1>
        <p className="text-lg md:text-xl text-muted-foreground max-w-md mx-auto">
          Bid your coins, build your team, battle it out.
          <br />
          <span className="text-primary font-semibold">Every auction is a mind game.</span>
        </p>
      </motion.div>

      {/* Verse selector */}
      <motion.div
        className="mb-4 sm:mb-8 w-full max-w-5xl relative z-10"
        initial={false}
      >
        <label className="block text-sm text-muted-foreground mb-2 text-center">
          Select Verse
        </label>
        <div className="grid grid-cols-2 gap-2 justify-items-center px-2 sm:grid-cols-3 sm:gap-4 lg:grid-cols-5">
          {verses.map((verse) => {
            const backdrop = getVerseThumbnail(verse);
            const selected = settings.verse === verse;
            return (
              <motion.button
                key={verse}
                type="button"
                onClick={() => selectVerse(verse)}
                aria-label={verse}
                className={`relative h-[68px] w-32 sm:h-20 md:h-24 md:w-40 rounded-xl overflow-hidden border-2 transition-all ${
                  selected ? 'border-primary' : 'border-border/60'
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <img
                  src={backdrop}
                  alt={verse}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div className={`absolute inset-0 ${selected ? 'bg-black/5' : 'bg-black/10'} transition-colors`} />
                {selected && (
                  <motion.div 
                    className="absolute inset-0 ring-2 ring-primary/60 rounded-xl"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  />
                )}
                {/* Verse name label */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/60 to-transparent py-1 px-2">
                  <p className={`text-xs font-display font-semibold text-center ${
                    selected ? 'text-primary' : 'text-white/90'
                  }`}>
                    {verse}
                  </p>
                </div>
              </motion.button>
            );
          })}
        </div>
      </motion.div>

      {/* Primary actions belong immediately after the verse choices. */}
      <motion.div
        className="mb-6 flex flex-col gap-4 w-full max-w-sm relative z-10"
        initial={false}
      >
        <Button
          size="lg"
          onClick={handlePlayVsCpu}
          className="w-full h-16 text-xl font-display font-bold bg-primary hover:bg-primary/90 text-primary-foreground"
        >
          Play vs CPU
        </Button>
        <Button
          variant="outline"
          size="lg"
          onClick={handleOpenLobby}
          className="w-full h-14 text-lg font-display border-player text-player hover:bg-player/10"
        >
          Host or Join Game
        </Button>
        <Button variant="outline" size="lg" onClick={() => navigate('/how-it-works')} className="w-full h-12 text-base font-display">
          How It Works
        </Button>
      </motion.div>

      <div className="mx-auto mb-6 grid w-full max-w-3xl gap-3 rounded-xl border border-border bg-card p-4 md:grid-cols-2 relative z-10">
        <Button
          size="lg"
          onClick={() => navigate('/creator')}
          className="h-14 text-base font-display font-bold bg-primary hover:bg-primary/90 text-primary-foreground"
        >
          Create a Verse
        </Button>
        <Button
          variant="outline"
          size="lg"
          onClick={() => navigate('/gallery')}
          className="h-14 text-base font-display font-bold border-primary/70 text-primary hover:bg-primary/10"
        >
          Browse Verse Gallery
        </Button>
      </div>

      {/* Support strip */}
      <motion.div
        className="mt-1 mb-6 w-full max-w-xl text-center relative z-10"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35 }}
      >
        <div className="px-5 py-4">
          <div className="mt-4 flex flex-wrap items-center justify-center gap-3 text-base font-display">
            <a
              href="https://discord.gg/Yet6UyaN4e"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white transition-[filter] hover:brightness-110"
              style={{ background: '#5865F2' }}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5">
                <path
                  fill="currentColor"
                  d="M19.55 5.39a17 17 0 0 0-4.21-1.3c-.18.33-.39.77-.53 1.11a16 16 0 0 0-4.58 0c-.14-.34-.35-.78-.53-1.11a17 17 0 0 0-4.22 1.3C2.94 8.15 2 10.84 2 13.5a17 17 0 0 0 5.55 2.79c.43-.6.82-1.24 1.13-1.91a10.8 10.8 0 0 1-1.78-.86c.15-.11.3-.22.43-.34a11.2 11.2 0 0 0 9.34 0c.14.12.28.23.43.34-.56.32-1.15.6-1.78.86.31.67.7 1.31 1.13 1.91A16.9 16.9 0 0 0 22 13.5c0-2.66-.94-5.35-2.45-8.11ZM9.6 13.46c-.66 0-1.2-.61-1.2-1.36 0-.75.53-1.36 1.2-1.36s1.2.61 1.2 1.36c0 .75-.53 1.36-1.2 1.36Zm4.8 0c-.66 0-1.2-.61-1.2-1.36 0-.75.54-1.36 1.2-1.36.67 0 1.2.61 1.2 1.36 0 .75-.53 1.36-1.2 1.36Z"
                />
              </svg>
              Join Discord
            </a>
            <a
              href="https://ko-fi.com/animedraft"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white transition-[filter] hover:brightness-110"
              style={{ background: '#1B8FC7' }}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5">
                <path
                  fill="currentColor"
                  d="M4 4h12a4 4 0 0 1 0 8h-1.2l-1.6 7.2a2 2 0 0 1-1.96 1.56H8.76a2 2 0 0 1-1.96-1.56L4 4Zm12 6a2 2 0 0 0 0-4h-1.1l.9 4H16Z"
                />
              </svg>
              Support on Ko-fi
              <span className="ml-1 rounded-full bg-white/15 px-2 py-0.5 text-[11px] uppercase tracking-wide">Just £1</span>
            </a>
            <a
              href="https://www.reddit.com/r/Anime_Draft/"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white transition-[filter] hover:brightness-110"
              style={{ background: '#E03D00' }}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5">
                <circle cx="9.25" cy="13" r="1.1" fill="currentColor" />
                <circle cx="14.75" cy="13" r="1.1" fill="currentColor" />
                <path d="M9 15.6c.9.65 2 .95 3 .95s2.1-.3 3-.95" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                <path d="M7.2 14.5c-.45-.4-.7-.93-.7-1.5 0-.78.47-1.45 1.16-1.76A4.9 4.9 0 0 1 12 9a4.9 4.9 0 0 1 4.34 2.24c.69.31 1.16.98 1.16 1.76 0 .57-.25 1.1-.7 1.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M13.1 8.55l.6-2.35 1.95.45" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="16.05" cy="6.55" r="1.15" fill="none" stroke="currentColor" strokeWidth="1.4" />
              </svg>
              Join the Reddit
            </a>
          </div>
          <p className="mt-3 text-base md:text-lg font-display text-foreground">
            Support me and future projects for just <span className="text-primary font-bold">£1</span>.
          </p>
          <p className="mt-1 text-xs md:text-sm text-muted-foreground">
            Join the community or chip in to keep new updates coming.
          </p>
        </div>
      </motion.div>

      {/* Game settings preview */}
      <motion.div
        className="mt-12 text-center text-sm text-muted-foreground relative z-10"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
      >
        <p>
          {settings.auctionCount} auctions · {settings.startingCoins} starting coins · {settings.bidTimer}s bid timer · first to {settings.battleFormat}
        </p>
      </motion.div>



      {/* Spacer to push footer down */}
      <div className="flex-1" />

      <Footer />
    </div>
  );
}

